import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export interface FooterData {
  companyInfo: {
    name: string;
    description: string;
    address: string;
    phone: string;
    email: string;
  };
  socialLinks: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    twitter?: string;
  };
  copyright: string;
  quickLinks: Array<{
    id: string;
    label: string;
    url: string;
  }>;
}

export interface FooterDocument extends FooterData {
  id: string;
}

// Type utility to convert FooterData to FooterDocument
const toFooterDocument = (data: FooterData, id: string): FooterDocument => ({
  ...data,
  id
});

// Type utility to convert FooterDocument to FooterData
const toFooterData = (doc: FooterDocument): FooterData => {
  const { id, ...data } = doc;
  return data;
};

const FOOTER_COLLECTION = 'footer_settings';
const FOOTER_DOC_ID = 'main_footer';

const defaultFooterData: FooterData = {
  companyInfo: {
    name: 'Ridgeline Homes',
    description: 'Building exceptional homes with unmatched quality and craftsmanship.',
    address: '123 Builder Street, City, State 12345',
    phone: '(555) 123-4567',
    email: 'info@ridgelinehomes.com'
  },
  socialLinks: {},
  copyright: `© ${new Date().getFullYear()} Ridgeline Homes. All rights reserved.`,
  quickLinks: [
    { id: '1', label: 'Our Projects', url: '/projects' },
    { id: '2', label: 'About Us', url: '/about' },
    { id: '3', label: 'Contact', url: '/contact' }
  ]
};

class FooterService {
  async getFooterData(): Promise<FooterData> {
    try {
      const docRef = doc(db, FOOTER_COLLECTION, FOOTER_DOC_ID);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        // If no footer data exists, create it with default values
        await this.updateFooterData(defaultFooterData);
        return defaultFooterData;
      }
      
      return toFooterData(docSnap.data() as FooterDocument);
    } catch (error) {
      console.error('Error fetching footer data:', error);
      throw new Error('Failed to fetch footer data');
    }
  }

  async updateFooterData(footerData: FooterData): Promise<void> {
    try {
      const docRef = doc(db, FOOTER_COLLECTION, FOOTER_DOC_ID);
      const footerDoc = toFooterDocument(footerData, FOOTER_DOC_ID);
      await setDoc(docRef, footerDoc);
    } catch (error) {
      console.error('Error updating footer data:', error);
      throw new Error('Failed to update footer data');
    }
  }
}

export const footerService = new FooterService(); 